import {
  Badge,
  Box,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiEdit2, FiHash, FiPlus, FiTrash2 } from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/tag-sku-editor")({
  component: TagSkuEditorPage,
})

interface SkuRow {
  id: string
  organisation_id: string
  value: string
  label?: string | null
  is_default?: boolean
  enabled?: boolean
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function TagSkuEditorPage() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  const listQuery = useQuery({
    queryKey: ["tag-sku-catalog", orgId],
    queryFn: async (): Promise<{ data: SkuRow[]; count: number }> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-skus-catalog/?limit=500`,
      )
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })
  const rows = listQuery.data?.data ?? []

  const [isOpen, setIsOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingIsDefault, setEditingIsDefault] = useState(false)
  const [draft, setDraft] = useState({ value: "", label: "" })

  const resetDraft = () => {
    setEditingId(null)
    setEditingIsDefault(false)
    setDraft({ value: "", label: "" })
  }
  const seedFromRow = (row: SkuRow) => {
    setEditingId(row.id)
    setEditingIsDefault(!!row.is_default)
    setDraft({ value: row.value, label: row.label ?? "" })
    setIsOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No organisation selected")
      const url = editingId
        ? `/api/v1/organizations/${orgId}/tag-skus-catalog/${editingId}`
        : `/api/v1/organizations/${orgId}/tag-skus-catalog/`
      const body = editingId
        ? {
            value: draft.value.trim(),
            label: draft.label.trim() || null,
          }
        : {
            organisation_id: orgId,
            value: draft.value.trim(),
            label: draft.label.trim() || null,
          }
      const res = await authedFetch(url, {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-sku-catalog", orgId] })
      queryClient.invalidateQueries({ queryKey: ["distinct-tag-skus"] })
      showToast(
        editingId
          ? t("tagSkuEditor.toast.updated", "SKU updated")
          : t("tagSkuEditor.toast.created", "SKU created"),
        t("tagSkuEditor.toast.saved", "Saved."),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t("tagSkuEditor.toast.errorTitle", "Could not save SKU"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error("No organisation selected")
      const res = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-skus-catalog/${id}`,
        { method: "DELETE" },
      )
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-sku-catalog", orgId] })
      queryClient.invalidateQueries({ queryKey: ["distinct-tag-skus"] })
      showToast(
        t("tagSkuEditor.toast.deleted", "SKU deleted"),
        t(
          "tagSkuEditor.toast.deletedBody",
          "Tags that referenced it have been moved to the default SKU.",
        ),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t("tagSkuEditor.toast.deleteErrorTitle", "Could not delete"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-tags" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("tagSkuEditor.back", "Back to Tags")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiHash fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("tagSkuEditor.title", "Tag SKU Editor")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "tagSkuEditor.subtitle",
              "Org-scoped catalog of GS1 SKUs. The default row can't be deleted — tags whose SKU is removed are reassigned to the default automatically. Tag.sku drives the DPP record join, so renaming a SKU here transparently cascades to every tag using it.",
            )}
          </Text>
        </Box>
      </Flex>

      <VisionCard>
        <Flex p={4} align="center" justify="space-between" flexWrap="wrap">
          <Heading size="md" color={sc.heading}>
            {t("tagSkuEditor.listTitle", "{{count}} total", {
              count: listQuery.data?.count ?? 0,
            })}
          </Heading>
          <VisionButton
            variant="brand"
            size="sm"
            onClick={() => {
              resetDraft()
              setIsOpen(true)
            }}
          >
            <FiPlus />
            <Text ml={1}>{t("tagSkuEditor.add", "Add SKU")}</Text>
          </VisionButton>
        </Flex>

        {listQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("tagSkuEditor.empty", "No SKUs yet.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagSkuEditor.col.sku", "GS1 SKU")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagSkuEditor.col.label", "Label")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagSkuEditor.col.default", "Default")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader w="40px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell
                      color={sc.heading}
                      fontFamily="mono"
                      fontSize="sm"
                    >
                      {row.value}
                    </Table.Cell>
                    <Table.Cell color={sc.textMuted} fontSize="sm">
                      {row.label ?? "—"}
                    </Table.Cell>
                    <Table.Cell>
                      {row.is_default && (
                        <Badge colorPalette="blue" size="sm">
                          {t("tagSkuEditor.defaultBadge", "default")}
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <VisionButton
                        variant="ghost"
                        size="xs"
                        onClick={() => seedFromRow(row)}
                      >
                        <FiEdit2 />
                      </VisionButton>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      <DialogRoot
        open={isOpen}
        onOpenChange={(d) => {
          if (!d.open) resetDraft()
          setIsOpen(d.open)
        }}
        size="md"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t("tagSkuEditor.dialog.editTitle", "Edit SKU")
                : t("tagSkuEditor.dialog.createTitle", "Add SKU")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("tagSkuEditor.field.value", "GS1 SKU")}
                </Text>
                <Input
                  value={draft.value}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, value: e.target.value }))
                  }
                  placeholder="00012345678905"
                  fontFamily="mono"
                  maxLength={255}
                  autoFocus
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("tagSkuEditor.field.label", "Label (optional)")}
                </Text>
                <Input
                  value={draft.label}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, label: e.target.value }))
                  }
                  placeholder="Patagonia Down Jacket M"
                  maxLength={200}
                />
              </Box>
              {editingIsDefault && (
                <Text fontSize="xs" color={sc.textMuted}>
                  {t(
                    "tagSkuEditor.field.defaultNote",
                    "This is the default SKU. It can be renamed but not deleted — tags whose SKU is removed are reassigned to it.",
                  )}
                </Text>
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex gap={2} w="100%" justify="space-between">
              <Box>
                {editingId && !editingIsDefault && (
                  <VisionButton
                    variant="ghost"
                    size="sm"
                    colorPalette="red"
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (
                        confirm(
                          t(
                            "tagSkuEditor.confirmDelete",
                            "Delete this SKU? Tags using it will be moved to the default SKU.",
                          ) as string,
                        )
                      ) {
                        deleteMutation.mutate(editingId)
                      }
                    }}
                  >
                    <FiTrash2 />
                    <Text ml={1}>
                      {t("tagSkuEditor.dialog.delete", "Delete")}
                    </Text>
                  </VisionButton>
                )}
              </Box>
              <Flex gap={2}>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false)
                    resetDraft()
                  }}
                >
                  {t("tagSkuEditor.dialog.cancel", "Cancel")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  size="sm"
                  disabled={
                    draft.value.trim().length === 0 || saveMutation.isPending
                  }
                  onClick={() => saveMutation.mutate()}
                >
                  {t("tagSkuEditor.dialog.save", "Save")}
                </VisionButton>
              </Flex>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
